import React, { useState, useEffect } from 'react';
import { 
  Building2, Mail, Phone, Globe, MapPin, Save, 
  AlertCircle, CheckCircle, Upload, FileText,
  Facebook, Instagram, Linkedin, Twitter
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { agencyService } from '../../services';
import './AgencySettings.css';

const AgencySettings = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  const [activeTab, setActiveTab] = useState('general');
  
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    email: '',
    phone: '',
    website: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    country: 'US',
    facebook: '',
    instagram: '',
    linkedin: '',
    twitter: '',
    logo: '',
    primaryColor: '#0066CC',
    secondaryColor: '#004499',
    agent_terms: ''
  });

  useEffect(() => {
    loadAgencyData();
  }, []);

  const loadAgencyData = async () => {
    try {
      setLoading(true);
      
      const { agency } = await agencyService.getSettings();
      if (agency) {
        setFormData({
          name: agency.name || '',
          code: agency.code || '',
          email: agency.email || '',
          phone: agency.phone || '',
          website: agency.website || '',
          address: agency.address || '',
          city: agency.city || '',
          state: agency.state || '',
          zipCode: agency.zip_code || '',
          country: agency.country || 'US',
          facebook: agency.facebook || '',
          instagram: agency.instagram || '',
          linkedin: agency.linkedin || '',
          twitter: agency.twitter || '',
          logo: agency.logo || '',
          primaryColor: agency.primary_color || '#0066CC',
          secondaryColor: agency.secondary_color || '#004499',
          agent_terms: agency.agent_terms || ''
        });
      }
    } catch (error) {
      console.error('Failed to load agency data:', error);
      setMessage({ type: 'error', text: 'Failed to load agency settings' });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      setMessage(null);
      
      await agencyService.updateSettings(formData);
      setMessage({ type: 'success', text: 'Agency settings updated successfully!' });
    } catch (error) {
      console.error('Failed to update agency settings:', error);
      setMessage({ type: 'error', text: 'Failed to update agency settings' });
    } finally {
      setSaving(false);
    }
  };

  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      // In production, this would upload to server
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData({ ...formData, logo: reader.result });
      };
      reader.readAsDataURL(file);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading agency settings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="agency-settings">
      <div className="settings-header">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Agency Settings</h1>
          <p className="text-gray-600 mt-1">Manage your agency's profile and preferences</p>
        </div>
      </div>

      {message && (
        <div className={`message ${message.type}`}>
          {message.type === 'success' ? <CheckCircle size={20} /> : <AlertCircle size={20} />}
          <span>{message.text}</span>
        </div>
      )}

      <div className="settings-tabs">
        <button
          className={`tab ${activeTab === 'general' ? 'active' : ''}`}
          onClick={() => setActiveTab('general')}
        >
          <Building2 size={20} />
          General Information
        </button>
        <button
          className={`tab ${activeTab === 'branding' ? 'active' : ''}`}
          onClick={() => setActiveTab('branding')}
        >
          <Globe size={20} />
          Branding & Social
        </button>
        <button
          className={`tab ${activeTab === 'terms' ? 'active' : ''}`}
          onClick={() => setActiveTab('terms')}
        >
          <FileText size={20} />
          Agent Terms
        </button>
      </div>

      <form onSubmit={handleSubmit} className="settings-form">
        {activeTab === 'general' && (
          <div className="form-section">
            <h2 className="section-title">General Information</h2>
            
            <div className="form-grid">
              <div className="form-group">
                <label>Agency Name</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                />
              </div>
              
              <div className="form-group">
                <label>Agency Code</label>
                <input
                  type="text"
                  value={formData.code}
                  disabled
                  className="disabled"
                />
              </div>
              
              <div className="form-group">
                <label>Email</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                />
              </div>
              
              <div className="form-group">
                <label>Phone</label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  required
                />
              </div>
              
              <div className="form-group">
                <label>Website</label>
                <input
                  type="url"
                  value={formData.website}
                  onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                  placeholder="https://www.example.com"
                />
              </div>
              
              <div className="form-group full-width">
                <label>Address</label>
                <input
                  type="text"
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                />
              </div>
              
              <div className="form-group">
                <label>City</label>
                <input
                  type="text"
                  value={formData.city}
                  onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                />
              </div>
              
              <div className="form-group">
                <label>State</label>
                <input
                  type="text"
                  value={formData.state}
                  onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                  maxLength="2"
                />
              </div>
              
              <div className="form-group">
                <label>ZIP Code</label>
                <input
                  type="text"
                  value={formData.zipCode}
                  onChange={(e) => setFormData({ ...formData, zipCode: e.target.value })}
                />
              </div>
            </div>
          </div>
        )}

        {activeTab === 'branding' && (
          <div className="form-section">
            <h2 className="section-title">Branding & Social Media</h2>
            
            <div className="logo-section">
              <label>Agency Logo</label>
              <div className="logo-upload">
                {formData.logo ? (
                  <img src={formData.logo} alt="Agency Logo" className="logo-preview" />
                ) : (
                  <div className="logo-placeholder">
                    <Upload size={40} className="text-gray-400" />
                    <p>Upload Logo</p>
                  </div>
                )}
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleLogoUpload}
                  className="file-input"
                />
              </div>
            </div>
            
            <div className="form-grid">
              <div className="form-group">
                <label>Primary Color</label>
                <div className="color-input-group">
                  <input
                    type="color"
                    value={formData.primaryColor}
                    onChange={(e) => setFormData({ ...formData, primaryColor: e.target.value })}
                  />
                  <input
                    type="text"
                    value={formData.primaryColor}
                    onChange={(e) => setFormData({ ...formData, primaryColor: e.target.value })}
                  />
                </div>
              </div>
              
              <div className="form-group">
                <label>Secondary Color</label>
                <div className="color-input-group">
                  <input
                    type="color"
                    value={formData.secondaryColor}
                    onChange={(e) => setFormData({ ...formData, secondaryColor: e.target.value })}
                  />
                  <input
                    type="text"
                    value={formData.secondaryColor}
                    onChange={(e) => setFormData({ ...formData, secondaryColor: e.target.value })}
                  />
                </div>
              </div>
            </div>
            
            <h3 className="subsection-title">Social Media Links</h3>
            <div className="form-grid">
              <div className="form-group">
                <label><Facebook size={16} /> Facebook</label>
                <input
                  type="url"
                  value={formData.facebook}
                  onChange={(e) => setFormData({ ...formData, facebook: e.target.value })}
                  placeholder="https://facebook.com/yourpage"
                />
              </div>
              
              <div className="form-group">
                <label><Instagram size={16} /> Instagram</label>
                <input
                  type="url"
                  value={formData.instagram}
                  onChange={(e) => setFormData({ ...formData, instagram: e.target.value })}
                  placeholder="https://instagram.com/yourpage"
                />
              </div>
              
              <div className="form-group">
                <label><Linkedin size={16} /> LinkedIn</label>
                <input
                  type="url"
                  value={formData.linkedin}
                  onChange={(e) => setFormData({ ...formData, linkedin: e.target.value })}
                  placeholder="https://linkedin.com/company/yourpage"
                />
              </div>
              
              <div className="form-group">
                <label><Twitter size={16} /> Twitter</label>
                <input
                  type="url"
                  value={formData.twitter}
                  onChange={(e) => setFormData({ ...formData, twitter: e.target.value })}
                  placeholder="https://twitter.com/yourpage"
                />
              </div>
            </div>
          </div>
        )}

        {activeTab === 'terms' && (
          <div className="form-section">
            <h2 className="section-title">Agent Agreement Terms</h2>
            <p className="section-description">
              These terms will be displayed to agents when they apply to join your agency.
            </p>
            
            <div className="form-group">
              <label>Agent Terms and Conditions</label>
              <textarea
                value={formData.agent_terms}
                onChange={(e) => setFormData({ ...formData, agent_terms: e.target.value })}
                rows={15}
                placeholder="Enter your agency's terms and conditions for agents..."
                className="terms-textarea"
              />
            </div>
          </div>
        )}

        <div className="form-actions">
          <button type="submit" className="save-button" disabled={saving}>
            {saving ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Saving...
              </>
            ) : (
              <>
                <Save size={20} />
                Save Settings
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default AgencySettings;
